import React from 'react';
import styled, { css } from 'styled-components';
import { Body, Heading3, Heading5, Variant } from '~/components/atom/Typography';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import Icon from '~/components/atom/Icon';
import Input from '~/components/molecule/Input';
import Dropdown from '~/components/molecule/Dropdown';
import JustificationContainer from '~/components/atom/JustificationContainer';
import {
  WidgetVisibilityAction,
  WidgetVisibilityConditionOperator,
  WidgetVisibilityConditionSubject,
  type WidgetVisibilityRulesFragment,
} from '~/graphql/types';
import { validateVisibilityRules } from '../../utils/validateVisibilityRules';

type VisibilityRule = WidgetVisibilityRulesFragment['rules'][number];
type VisibilityCondition = VisibilityRule['conditions'][number];

const text = {
  title: 'Zichtbaarheidsregels',
  description:
    'Bepaal op welke pagina\u2019s van uw website de widget wordt getoond of verborgen. Regels worden op volgorde gecontroleerd: de eerste overeenkomende regel bepaalt het resultaat. Alle waarden worden automatisch omgezet naar kleine letters (bijv. MijnDomein.nl/Test wordt mijndomain.nl/test).',
  emptyState: 'Geen regels ingesteld. De widget wordt op alle pagina\u2019s getoond.',
  addRule: 'Regel toevoegen',
  addCondition: 'Voorwaarde toevoegen',
  ruleLabel: (index: number) => `Regel ${index + 1}`,
  actionLabel: 'Actie',
  subjectLabel: 'Vergelijk met',
  operatorLabel: 'Operator',
  valueLabel: 'Waarde',
  conditionJoiner: 'EN',
};

const VALUE_PLACEHOLDER: Record<WidgetVisibilityConditionSubject, string> = {
  [WidgetVisibilityConditionSubject.Host]: 'bijv. example.com',
  [WidgetVisibilityConditionSubject.Path]: 'bijv. /verkoper/nieuws',
  [WidgetVisibilityConditionSubject.Url]: 'bijv. https://example.com/verkoper/nieuws',
};

const ACTION_OPTIONS = [
  { key: WidgetVisibilityAction.Show, label: '✅ Tonen', payload: WidgetVisibilityAction.Show },
  { key: WidgetVisibilityAction.Hide, label: '🚫 Verbergen', payload: WidgetVisibilityAction.Hide },
];

const SUBJECT_OPTIONS = [
  { key: WidgetVisibilityConditionSubject.Host, label: 'Host (domein)', payload: WidgetVisibilityConditionSubject.Host },
  { key: WidgetVisibilityConditionSubject.Path, label: 'Pad', payload: WidgetVisibilityConditionSubject.Path },
  { key: WidgetVisibilityConditionSubject.Url, label: 'Volledige URL', payload: WidgetVisibilityConditionSubject.Url },
];

const OPERATOR_OPTIONS = [
  { key: WidgetVisibilityConditionOperator.Equals, label: 'Is gelijk aan', payload: WidgetVisibilityConditionOperator.Equals },
  { key: WidgetVisibilityConditionOperator.NotEquals, label: 'Is niet gelijk aan', payload: WidgetVisibilityConditionOperator.NotEquals },
  { key: WidgetVisibilityConditionOperator.Contains, label: 'Bevat', payload: WidgetVisibilityConditionOperator.Contains },
  { key: WidgetVisibilityConditionOperator.NotContains, label: 'Bevat niet', payload: WidgetVisibilityConditionOperator.NotContains },
  { key: WidgetVisibilityConditionOperator.StartsWith, label: 'Begint met', payload: WidgetVisibilityConditionOperator.StartsWith },
  { key: WidgetVisibilityConditionOperator.NotStartsWith, label: 'Begint niet met', payload: WidgetVisibilityConditionOperator.NotStartsWith },
  { key: WidgetVisibilityConditionOperator.EndsWith, label: 'Eindigt op', payload: WidgetVisibilityConditionOperator.EndsWith },
  { key: WidgetVisibilityConditionOperator.NotEndsWith, label: 'Eindigt niet op', payload: WidgetVisibilityConditionOperator.NotEndsWith },
  { key: WidgetVisibilityConditionOperator.Regex, label: 'Regex', payload: WidgetVisibilityConditionOperator.Regex },
  { key: WidgetVisibilityConditionOperator.NotRegex, label: 'Geen regex-match', payload: WidgetVisibilityConditionOperator.NotRegex },
];

const createCondition = (): VisibilityCondition => ({
  __typename: 'WidgetVisibilityCondition',
  subject: WidgetVisibilityConditionSubject.Path,
  operator: WidgetVisibilityConditionOperator.Equals,
  value: '',
});

const createRule = (): VisibilityRule => ({
  __typename: 'WidgetVisibilityRule',
  action: WidgetVisibilityAction.Show,
  conditions: [createCondition()],
});

export type Props = {
  visibilityRules: WidgetVisibilityRulesFragment;
  onChange: (visibilityRules: WidgetVisibilityRulesFragment) => void;
  disabled?: boolean;
};

const VisibilityRulesEditor: React.FCC<Props> = ({
  visibilityRules,
  onChange,
  disabled = false,
}) => {
  const errors = validateVisibilityRules(visibilityRules);

  const conditionError = (ruleIndex: number, conditionIndex: number) =>
    errors.find(
      e => e.ruleIndex === ruleIndex && e.conditionIndex === conditionIndex,
    );

  const ruleError = (ruleIndex: number) =>
    errors.find(
      e => e.ruleIndex === ruleIndex && e.conditionIndex === undefined,
    );

  const commit = (rules: Array<VisibilityRule>) => {
    onChange({ __typename: 'WidgetVisibilityRules', rules });
  };

  const moveRule = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const rules = [...visibilityRules.rules];
    const [moved] = rules.splice(index, 1);
    rules.splice(newIndex, 0, moved);
    commit(rules);
  };

  const updateRule = (ruleIndex: number, patch: Partial<VisibilityRule>) => {
    const rules = [...visibilityRules.rules];
    rules[ruleIndex] = { ...rules[ruleIndex], ...patch };
    commit(rules);
  };

  const removeRule = (ruleIndex: number) => {
    commit(visibilityRules.rules.filter((_, i) => i !== ruleIndex));
  };

  const updateCondition = (
    ruleIndex: number,
    conditionIndex: number,
    patch: Partial<VisibilityCondition>,
  ) => {
    const rules = [...visibilityRules.rules];
    const conditions = [...rules[ruleIndex].conditions];
    conditions[conditionIndex] = { ...conditions[conditionIndex], ...patch };
    rules[ruleIndex] = { ...rules[ruleIndex], conditions };
    commit(rules);
  };

  const addCondition = (ruleIndex: number) => {
    const rules = [...visibilityRules.rules];
    rules[ruleIndex] = {
      ...rules[ruleIndex],
      conditions: [...rules[ruleIndex].conditions, createCondition()],
    };
    commit(rules);
  };

  const removeCondition = (ruleIndex: number, conditionIndex: number) => {
    const rules = [...visibilityRules.rules];
    const conditions = rules[ruleIndex].conditions.filter(
      (_, i) => i !== conditionIndex,
    );
    if (conditions.length === 0) return;
    rules[ruleIndex] = { ...rules[ruleIndex], conditions };
    commit(rules);
  };

  const hasRules = visibilityRules.rules.length > 0;

  return (
    <Section>
      <Heading3 variant={Variant.primary} margin={[null, null, 'xs', null]}>
        {text.title}
      </Heading3>
      <Body margin={[null, null, 'm', null]}>{text.description}</Body>

      {!hasRules && (
        <EmptyState>
          <Body>{text.emptyState}</Body>
        </EmptyState>
      )}

      {visibilityRules.rules.map((rule, ruleIndex) => {
        const isFirst = ruleIndex === 0;
        const isLast = ruleIndex === visibilityRules.rules.length - 1;
        const hasError =
          !!ruleError(ruleIndex) ||
          rule.conditions.some((_, ci) => !!conditionError(ruleIndex, ci));

        return (
          <RuleCard key={ruleIndex} $hasError={hasError}>
            <RuleHeader>
              <JustificationContainer align="center" gap="base">
                <ReorderControls direction="column" align="center">
                  <ArrowButton
                    onClick={() => moveRule(ruleIndex, 'up')}
                    disabled={disabled || isFirst}
                  >
                    <Icon name="arrow" />
                  </ArrowButton>
                  <ArrowButton
                    onClick={() => moveRule(ruleIndex, 'down')}
                    disabled={disabled || isLast}
                  >
                    <Icon name="arrow-down" />
                  </ArrowButton>
                </ReorderControls>
                <Heading5 margin={[null]} variant={Variant.primary}>
                  {text.ruleLabel(ruleIndex)}
                </Heading5>
                <ActionDropdown
                  size="medium"
                  options={ACTION_OPTIONS}
                  selectedOptionIdx={ACTION_OPTIONS.findIndex(
                    o => o.payload === rule.action,
                  )}
                  onChange={({ option }) =>
                    updateRule(ruleIndex, { action: option.payload })
                  }
                  disabled={disabled}
                  withDefaultFirstOption={false}
                />
              </JustificationContainer>
              <TextButton
                size="medium"
                appearance="danger"
                icon="trashcan"
                onClick={() => removeRule(ruleIndex)}
                disabled={disabled}
              />
            </RuleHeader>

            <ConditionsArea>
              {rule.conditions.map((condition, conditionIndex) => {
                const error = conditionError(ruleIndex, conditionIndex);
                return (
                  <React.Fragment key={conditionIndex}>
                    {conditionIndex > 0 && (
                      <ConditionJoiner>
                        <JoinerBadge>{text.conditionJoiner}</JoinerBadge>
                      </ConditionJoiner>
                    )}
                    <ConditionRow>
                      <Dropdown
                        size="medium"
                        label={conditionIndex === 0 ? text.subjectLabel : undefined}
                        options={SUBJECT_OPTIONS}
                        selectedOptionIdx={SUBJECT_OPTIONS.findIndex(
                          o => o.payload === condition.subject,
                        )}
                        onChange={({ option }) =>
                          updateCondition(ruleIndex, conditionIndex, {
                            subject: option.payload,
                          })
                        }
                        disabled={disabled}
                        width="160px"
                        withDefaultFirstOption={false}
                      />
                      <Dropdown
                        size="medium"
                        label={conditionIndex === 0 ? text.operatorLabel : undefined}
                        options={OPERATOR_OPTIONS}
                        selectedOptionIdx={OPERATOR_OPTIONS.findIndex(
                          o => o.payload === condition.operator,
                        )}
                        onChange={({ option }) =>
                          updateCondition(ruleIndex, conditionIndex, {
                            operator: option.payload,
                          })
                        }
                        disabled={disabled}
                        width="180px"
                        withDefaultFirstOption={false}
                      />
                      <ValueInputWrapper>
                        <Input
                          label={
                            conditionIndex === 0
                              ? { text: text.valueLabel }
                              : undefined
                          }
                          width="100%"
                          size="medium"
                          placeholder={VALUE_PLACEHOLDER[condition.subject]}
                          value={condition.value ?? ''}
                          disabled={disabled}
                          onChange={e =>
                            updateCondition(ruleIndex, conditionIndex, {
                              value: e.target.value.toLowerCase(),
                            })
                          }
                          externalErrors={
                            error?.message ? [error.message] : []
                          }
                        />
                      </ValueInputWrapper>
                      {rule.conditions.length > 1 && (
                        <DeleteConditionButton
                          $hasLabel={conditionIndex === 0}
                        >
                          <TextButton
                            size="medium"
                            appearance="danger"
                            icon="trashcan"
                            onClick={() =>
                              removeCondition(ruleIndex, conditionIndex)
                            }
                            disabled={disabled}
                          />
                        </DeleteConditionButton>
                      )}
                    </ConditionRow>
                  </React.Fragment>
                );
              })}
            </ConditionsArea>

            {ruleError(ruleIndex) && (
              <ErrorText>{ruleError(ruleIndex)?.message}</ErrorText>
            )}

            <TextButton
              size="medium"
              appearance="primary"
              icon="plus"
              label={text.addCondition}
              onClick={() => addCondition(ruleIndex)}
              disabled={disabled}
              margin={['s', null, null, null]}
            />
          </RuleCard>
        );
      })}

      <Button
        size="medium"
        appearance="secondary"
        icon="plus"
        label={text.addRule}
        onClick={() => commit([...visibilityRules.rules, createRule()])}
        disabled={disabled}
      />
    </Section>
  );
};

const Section = styled.div(
  ({ theme }) => css`
    margin-top: ${theme.space('xl')};
  `,
);

const EmptyState = styled.div(
  ({ theme }) => css`
    padding: ${theme.space('m')} ${theme.space('l')};
    margin-bottom: ${theme.space('m')};
    border: 1px dashed ${theme.color('tertiary')};
    border-radius: ${theme.getTokens().border.radius.base};
    text-align: center;
  `,
);

const RuleCard = styled.div<{ $hasError?: boolean }>(
  ({ theme, $hasError }) => css`
    margin-bottom: ${theme.space('m')};
    border: ${theme.getTokens().border.width.s} solid
      ${$hasError ? theme.color('danger') : theme.color('tertiary', 'light')};
    border-radius: ${theme.getTokens().border.radius.base};
    background: ${theme.color('white')};
    overflow: hidden;
  `,
);

const RuleHeader = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.space('s')} ${theme.space('m')};
    background: ${theme.color('tertiary', 'translucent')};
    border-bottom: 1px solid ${theme.color('tertiary', 'light')};
  `,
);

const ReorderControls = styled(JustificationContainer)(
  () => css`
    gap: 2px;
  `,
);

const ArrowButton = styled.button<{ disabled?: boolean }>(
  ({ theme, disabled }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 16px;
    padding: 0;
    border: none;
    background: none;
    cursor: ${disabled ? 'default' : 'pointer'};
    opacity: ${disabled ? 0.25 : 1};
    color: ${theme.color('primary')};
    font-size: ${theme.fontSize('base')};
    transition: opacity 0.15s ease;

    &:hover:not(:disabled) {
      opacity: 0.7;
    }

    svg {
      width: 12px;
      height: 12px;
    }
  `,
);

const ActionDropdown = styled(Dropdown)(
  () => css`
    width: 140px;
  `,
);

const ConditionsArea = styled.div(
  ({ theme }) => css`
    padding: ${theme.space('m')};
  `,
);

const ConditionRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.space('s')};
  `,
);

const ConditionJoiner = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    padding: ${theme.space('xs')} 0;
  `,
);

const JoinerBadge = styled.span(
  ({ theme }) => css`
    font-size: ${theme.fontSize('xs')};
    font-weight: ${theme.fontWeight('semiBold')};
    color: ${theme.color('tertiary', 'dark')};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `,
);

const ValueInputWrapper = styled.div`
  flex: 1;
  min-width: 120px;
`;

const DeleteConditionButton = styled.div<{ $hasLabel: boolean }>(
  ({ $hasLabel }) => css`
    display: flex;
    align-items: center;
    padding-top: ${$hasLabel ? '22px' : '0'};
  `,
);

const ErrorText = styled(Body)(
  ({ theme }) => css`
    color: ${theme.color('danger')};
    padding: 0 ${theme.space('m')} ${theme.space('s')};
  `,
);

export default VisibilityRulesEditor;
